import {
  elementIdOf,
  passEmptyPatterns,
  useFieldValidation,
  useCardSessionControls,
} from '@littlebird/action-cards-view/react';
import { useId, useRef } from 'react';

type ValidationConfig = NonNullable<Parameters<typeof useFieldValidation>[1]>;
type ValidateOn = 'change' | 'blur' | 'submit';

export const useElementId = (props: Record<string, unknown>): string => {
  const generated = useId();
  return elementIdOf(props) ?? generated;
};

export const useFieldChecks = ({
  path,
  checks,
  validateOn,
  value,
  serverError,
}: {
  path: string | undefined;
  checks: ValidationConfig['checks'] | undefined;
  validateOn: ValidateOn | undefined;
  value?: string;
  serverError?: string;
}) => {
  const controls = useCardSessionControls();
  const when = validateOn ?? 'blur';
  const active =
    path !== undefined && checks !== undefined && checks.length > 0;
  // Registered even when empty, so a run validates the checks the value has now
  const { errors, validate, state, clear } = useFieldValidation(
    path ?? '',
    path !== undefined && checks !== undefined
      ? { checks: passEmptyPatterns(checks), validateOn: when }
      : undefined
  );
  const shownError = useRef(false);
  if (errors.length > 0 || serverError) shownError.current = true;
  const error = active ? errors[0] : undefined;
  // A too-long value says how long it is now, so the user sees how far over it is
  const overLength =
    value !== undefined &&
    checks?.some(
      (check) => check.type === 'maxLength' && check.message === error
    )
      ? value.length
      : undefined;
  return {
    error:
      error !== undefined && overLength !== undefined
        ? `${error} · ${overLength.toLocaleString()}`
        : error,
    afterChange: () => {
      if (path) controls.fieldChanged(path);
      if (
        active &&
        (when === 'change' || state.validated || shownError.current)
      )
        validate();
    },
    onBlur: () => {
      if (active && when === 'blur') validate();
    },
    // A value's checks can change with it, so a clear drops what the old ones recorded
    clearErrors: clear,
  };
};

export const fieldErrorId = (id: string) => `${id}-error`;

// An editable field's label points at its control; a read-only value is named by it
export const labelIdOf = (id: string) => `${id}-label`;
